<template>
  <nav
    aria-label="Main"
    class="flex h-full min-h-0 w-full flex-col gap-0.5 px-3 pb-2.5 pt-3.5 text-surface-600"
  >
    <!-- Twine owns the top slot; the tenant sits at the foot, beside the account it belongs to. -->
    <div class="focus-row flex items-center justify-between px-1 pb-3">
      <Link
        aria-label="Home"
        class="flex h-8 items-center rounded-lg px-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        :href="homePath"
        @click="emit('navigate')"
      >
        <!-- The brand logos from twinepos.com/brand, with the name: navy in light, pink in dark. The folded rail shows the mark alone. -->
        <NavyLogo class="block h-[22px] w-auto dark:hidden" />
        <PinkLogo class="hidden h-[22px] w-auto dark:block" />
      </Link>
      <TwnBaseBtn
        v-tooltip.right="closeTooltip"
        :aria-label="closeLabel"
        icon="left_panel_close"
        severity="secondary"
        size="small"
        variant="text"
        @click="closeOrFold"
      />
    </div>

    <!-- Raised like the current page: a real field on the ground, with its shortcut as two keycaps. -->
    <button
      class="focus-row mb-2.5 flex h-[34px] w-full items-center gap-2 rounded-lg bg-surface-0 pl-2.5 pr-1.5 text-[13px] text-surface-500 shadow-sm ring-1 ring-surface-200 transition-colors hover:text-surface-700 hover:ring-surface-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
      style="--row: 1"
      type="button"
      @click="openCommandPalette"
    >
      <MaterialIcon
        ligature="search"
        size="1rem"
      />
      Search
      <span class="ml-auto flex gap-[3px]">
        <TwnKbd
          v-for="key in SEARCH_KEYS"
          :key="key"
          size="xs"
          tone="muted"
          >{{ key }}</TwnKbd
        >
      </span>
    </button>

    <!-- Settings is its own area: the list swaps to the settings pages, with a way back. -->
    <!-- Both lists scroll rather than squeeze: a flex column shrinks its rows to their text before it overflows. -->
    <div
      v-if="isSettingsArea"
      class="focus-row flex min-h-0 flex-1 flex-col gap-px overflow-y-auto [&>*]:shrink-0"
      style="--row: 2"
    >
      <TwnFocusSidebarLink
        :path="homePath"
        variant="space"
        @navigate="emit('navigate')"
      >
        <MaterialIcon
          ligature="arrow_back"
          size="1.125rem"
        />
        <span class="truncate">Back to {{ tenantName }}</span>
      </TwnFocusSidebarLink>
      <p class="px-2 pb-1 pt-2 text-[15px] font-semibold text-surface-900">Settings</p>
      <template
        v-for="section in settingsSections"
        :key="section.key"
      >
        <p class="px-2.5 pb-1 pt-3 text-xs font-medium text-surface-500">
          {{ section.name }}
        </p>
        <TwnFocusSidebarLink
          v-for="item in section.items"
          :key="item.path"
          :is-current="isMenuItemActive(item, currentPath)"
          :path="item.path"
          @navigate="emit('navigate')"
        >
          <span class="line-clamp-2">{{ item.name }}</span>
        </TwnFocusSidebarLink>
      </template>
    </div>

    <div
      v-else
      class="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto [&>*]:shrink-0"
    >
      <template
        v-for="section in spaces"
        :key="section.key"
      >
        <!-- A space with no page list is itself the current page, so it takes the selected tint like one. -->
        <TwnFocusSidebarLink
          :class="spaceRowClass(section)"
          :aria-expanded="drawerExpanded(section)"
          :is-action="isDrawerToggle(section)"
          :is-current="isOpen(section)"
          :path="spacePath(section)"
          :style="{ '--row': rowOrder.get(section.key) }"
          class="focus-row"
          variant="space"
          @click="toggleDrawerSpace(section)"
          @navigate="emit('navigate')"
        >
          <!-- The open space's icon fills, as the POS rail's selected icon does. -->
          <MaterialIcon
            :class="spaceIconClass(section)"
            :is-filled="isOpen(section)"
            :ligature="section.icon"
            size="1rem"
          />
          <span class="truncate">{{ section.name }}</span>
        </TwnFocusSidebarLink>

        <!-- The pages slide open or fold away (transitions.scss). -->
        <Transition
          :duration="PAGES_DURATION"
          name="focus-pages"
        >
          <div
            v-if="hasOpenPages(section)"
            class="focus-pages"
          >
            <div class="min-h-0 overflow-hidden">
              <TwnFocusSidebarPages
                :current-path="currentPath"
                :row-order="rowOrder"
                :section="section"
                @navigate="emit('navigate')"
              />
            </div>
          </div>
        </Transition>
      </template>
    </div>

    <div
      class="focus-row mt-2 flex flex-col gap-px"
      :style="{ '--row': FOCUS_SWEEP_LAST_ROW }"
    >
      <TwnFocusSidebarLink
        v-if="isSettingsLinkShown"
        :path="settingsPath"
        variant="space"
        @navigate="emit('navigate')"
      >
        <MaterialIcon
          class="text-surface-500"
          ligature="settings"
          size="1rem"
        />
        <span>Settings</span>
      </TwnFocusSidebarLink>
      <TwnFocusSidebarTenantLinks />
    </div>

    <div
      class="focus-row mt-2"
      :style="{ '--row': FOCUS_SWEEP_LAST_ROW }"
    >
      <TwnFocusSidebarUserMenu />
    </div>
  </nav>
</template>

<script lang="ts" setup>
import { Link } from '@inertiajs/vue3'
import { computed, ref } from 'vue'

import type {
  TwnFocusSidebarPanelEmits,
  TwnFocusSidebarPanelProps
} from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-panel/TwnFocusSidebarPanel.types'
import type { MenuSection } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'

import NavyLogo from '@/assets/svgs/navy-logo.svg?component'
import PinkLogo from '@/assets/svgs/pink-logo.svg?component'
import TwnBaseBtn from '@/components/atoms/buttons/twn-base-btn/TwnBaseBtn.vue'
import MaterialIcon from '@/components/atoms/icons/material-icon/MaterialIcon.vue'
import TwnKbd from '@/components/atoms/typography/twn-kbd/TwnKbd.vue'
import { useTailwindBreakpoints } from '@/composables/use-tailwind-breakpoints/useTailwindBreakpoints'
import TwnFocusSidebarLink from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-link/TwnFocusSidebarLink.vue'
import {
  FOCUS_PAGES_ENTER_MS,
  FOCUS_PAGES_LEAVE_MS
} from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-pages/TwnFocusSidebarPages.consts'
import TwnFocusSidebarPages from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-pages/TwnFocusSidebarPages.vue'
import TwnFocusSidebarTenantLinks from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-tenant-links/TwnFocusSidebarTenantLinks.vue'
import TwnFocusSidebarUserMenu from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-user-menu/TwnFocusSidebarUserMenu.vue'
import { useCurrentPath } from '@/domains/admin-panel/composables/use-current-path/useCurrentPath'
import { useMenuStore } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'
import { useSidebarPreferencesStore } from '@/domains/admin-panel/store/use-sidebar-preferences-store/useSidebarPreferencesStore'
import { FOCUS_SWEEP_LAST_ROW } from '@/domains/admin-panel/utils/focus-sidebar/focusSidebar.consts'
import {
  firstPathOf,
  isMenuItemActive,
  isSectionCurrent,
  isSinglePageSection
} from '@/domains/admin-panel/utils/focus-sidebar/focusSidebar.utils'
import { useCommandPalette } from '@/domains/command-center/composables/use-command-palette/useCommandPalette'
import { useTenantName } from '@/domains/tenant/composables/use-tenant-name/useTenantName'
import { TWINE_ROUTES } from '@/types/const/routes.consts'
import { MODIFIER_LABEL } from '@/utils/keyboard-util/keyboardUtil'

const props = defineProps<TwnFocusSidebarPanelProps>()
const emit = defineEmits<TwnFocusSidebarPanelEmits>()

const homePath = TWINE_ROUTES.HOME.ROOT
const SEARCH_KEYS = [MODIFIER_LABEL, 'K'] as const
const PAGES_DURATION = { enter: FOCUS_PAGES_ENTER_MS, leave: FOCUS_PAGES_LEAVE_MS } as const
// null follows the page; ALL_SPACES_CLOSED is a drawer tap that closed them all.
const ALL_SPACES_CLOSED = ''

// Composables
const menuStore = useMenuStore()
const { open: openCommandPalette } = useCommandPalette()
const { isLargeDesktop } = useTailwindBreakpoints()
const sidebarPreferences = useSidebarPreferencesStore()
const currentPath = useCurrentPath()
const tenantName = useTenantName()

// State
// One space is open: the page's, from the server's active flags, unless a drawer tap chose another.
const drawerOpenKey = ref<string | null>(null)

// Computed
const spaces = computed(() => menuStore.mainMenuMenuSections)
const settingsSections = computed(() => menuStore.serverSettingsMenuSections)
const settingsPath = computed(() => settingsSections.value.map(firstPathOf).find(Boolean))
const isSettingsArea = computed(() =>
  settingsSections.value.some(section => isSectionCurrent(section, currentPath.value))
)
// Inside Settings the foot's own link would point at the page already open.
const isSettingsLinkShown = computed(() => !!settingsPath.value && !isSettingsArea.value)

// Desktop folds to the rail; the drawer has no rail, so the same button closes it.
const closeTooltip = computed(() => (isLargeDesktop.value ? 'Fold the sidebar ([)' : 'Close the menu'))
const closeLabel = computed(() => (isLargeDesktop.value ? 'Fold the sidebar' : 'Close the menu'))

// The first-load sweep runs top to bottom: logo 0, search 1, then each space and its open pages in reading order.
const rowOrder = computed(() => {
  const order = new Map<string | undefined, number>()
  let next = 2
  for (const section of spaces.value) {
    order.set(section.key, next++)
    if (hasOpenPages(section)) section.items.forEach(item => order.set(item.path, next++))
  }
  return order
})

// Functions
const isOpen = (section: MenuSection): boolean =>
  (drawerOpenKey.value ?? menuStore.currentActiveNav?.key) === section.key
const hasOpenPages = (section: MenuSection): boolean => isOpen(section) && !isSinglePageSection(section)
// A space with no page list is itself the current page, so it takes the selected tint like one.
const spaceRowClass = (section: MenuSection): string =>
  isOpen(section) && isSinglePageSection(section) ? 'bg-selected !text-selected-foreground' : ''
const spaceIconClass = (section: MenuSection): string =>
  isOpen(section) ? 'text-selected-foreground' : 'text-surface-500'

// A space with pages only opens them in the drawer, so a tap there never closes it on a page the user did not pick.
const isDrawerToggle = (section: MenuSection): boolean => !!props.isDrawer && !isSinglePageSection(section)
const spacePath = (section: MenuSection): string | undefined =>
  isDrawerToggle(section) ? undefined : firstPathOf(section)
// Only a toggle states whether it is expanded; a link opens nothing.
const drawerExpanded = (section: MenuSection): boolean | undefined =>
  isDrawerToggle(section) ? isOpen(section) : undefined
const toggleDrawerSpace = (section: MenuSection) => {
  if (!isDrawerToggle(section)) return

  drawerOpenKey.value = isOpen(section) ? ALL_SPACES_CLOSED : section.key
}

const closeOrFold = () => (isLargeDesktop.value ? sidebarPreferences.setMode('folded') : menuStore.closeMobileMenu())
</script>
